import api from './api';

// Helper function to get notifications from localStorage
const getNotificationsFromLocalStorage = (userId) => {
  try {
    // Get current user's notifications
    const userStorageKey = `notifications_${userId}`;
    return JSON.parse(localStorage.getItem(userStorageKey) || '[]');
  } catch (error) {
    console.error('Error getting notifications from localStorage:', error);
    return [];
  }
};

// Helper function to save notifications to localStorage
const saveNotificationsToLocalStorage = (userId, notifications) => {
  try {
    const userStorageKey = `notifications_${userId}`;
    localStorage.setItem(userStorageKey, JSON.stringify(notifications));
  } catch (error) {
    console.error('Error saving notifications to localStorage:', error);
  }
};

// Generate a unique ID for notifications when using localStorage
const generateLocalId = () => `notification-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

// Helper to get user ID from auth state
const getUserId = () => {
  try {
    // Get token from localStorage
    const token = localStorage.getItem('token');
    if (!token) return null;
    
    // Get user info from localStorage
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) return null;
    
    // First try to get the ID as a direct value
    if (user.id) {
      // Handle object IDs (MongoDB ObjectId with _id field)
      if (typeof user.id === 'object') {
        if (user.id._id) {
          return user.id._id;
        }
        if (user.id.id) {
          return user.id.id;
        }
        if (user.id.username) {
          return user.id.username;
        }
      }
      
      // If it's a string, use it directly
      if (typeof user.id === 'string') {
        return user.id;
      }
    }
    
    // Fallback to username if available
    if (user.username) {
      return user.username;
    }
    
    return 'guest';
  } catch (error) {
    console.error('Error getting user ID:', error);
    return 'guest'; // Return a default ID instead of null to ensure we can still access localStorage
  }
};

// Check if the notification API is available
let isApiAvailable = false;

// Function to check API availability
const checkApiAvailability = async () => {
  try {
    const response = await api.get('/notifications/health');
    isApiAvailable = response.data && response.data.status === 'ok';
    console.log('Notification API status:', isApiAvailable ? 'available' : 'unavailable');
    return isApiAvailable;
  } catch (error) {
    console.error('Notification API is not available:', error);
    isApiAvailable = false;
    return false;
  }
};

// Try to check API availability on load
checkApiAvailability().catch(() => {
  console.log('Using localStorage for notifications due to API unavailability');
});

// Notification service for handling notification API calls
const notificationService = {
  // Check if the API is available
  checkApiHealth: checkApiAvailability,
  
  // Cleanup old notifications
  cleanupOldNotifications: async () => {
    try {
      const response = await api.post('/notifications/cleanup');
      console.log('Cleanup result:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error during cleanup:', error);
      return { error: error.message };
    }
  },
  
  // Remove specific scan completion notifications by scan IDs
  removeSpecificScanNotifications: async (scanIds) => {
    const userId = getUserId();
    let removedCount = 0;
    
    // Try to remove from API
    try {
      for (const scanId of scanIds) {
        const response = await api.delete(`/notifications/scan/${scanId}`);
        if (response.data.success) {
          removedCount++;
        }
      }
    } catch (error) {
      console.error('Error removing notifications from API:', error);
    }
    
    // Also remove from localStorage
    if (userId) {
      const notifications = getNotificationsFromLocalStorage(userId);
      const filteredNotifications = notifications.filter(notification => 
        !(notification.type === 'SCAN_COMPLETED' && scanIds.includes(notification.scanId))
      );
      
      const localRemovedCount = notifications.length - filteredNotifications.length;
      saveNotificationsToLocalStorage(userId, filteredNotifications);
      removedCount += localRemovedCount;
      
      console.log(`Removed ${localRemovedCount} scan notifications from localStorage`);
    }
    
    return { removedCount, scanIds };
  },
  // Get all notifications for the current user
  getNotifications: async () => {
    const userId = getUserId();
    let apiNotifications = [];
    let localNotifications = [];
    
    // Check if API is available first
    if (!isApiAvailable) {
      await checkApiAvailability();
    }
    
    // If API is available, try to use it
    if (isApiAvailable) {
      try {
        const response = await api.get('/notifications');
        apiNotifications = response.data || [];
        console.log(`Retrieved ${apiNotifications.length} notifications from API`);
        
        // Filter out blocked scan completion notifications before syncing or returning
        const blockedScanIds = ['test430_344473', 'test5_251692', 'test45', 'test', 'oyo_990845', 'Scan_154432', 'housing_819875'];
        apiNotifications = apiNotifications.filter(notification => {
          if (notification.type === 'SCAN_COMPLETED' && blockedScanIds.includes(notification.scanId)) {
            console.log(`Filtering out blocked scan notification: ${notification.scanId}`);
            return false;
          }
          return true;
        });
        
        // If we got notifications from API, also sync them to localStorage
        if (userId && Array.isArray(apiNotifications)) {
          saveNotificationsToLocalStorage(userId, apiNotifications);
        }
        
        return apiNotifications;
      } catch (error) {
        console.error('Error fetching notifications from API:', error);
        isApiAvailable = false; // Mark API as unavailable for future calls
      }
    }
    
    // Fallback to localStorage
    if (userId) {
      localNotifications = getNotificationsFromLocalStorage(userId);
      
      // Also filter blocked notifications from localStorage
      const blockedScanIds = ['test430_344473', 'test5_251692', 'test45', 'test', 'oyo_990845', 'Scan_154432', 'housing_819875'];
      localNotifications = localNotifications.filter(notification => {
        if (notification.type === 'SCAN_COMPLETED' && blockedScanIds.includes(notification.scanId)) {
          console.log(`Filtering out blocked scan notification from localStorage: ${notification.scanId}`);
          return false;
        }
        return true;
      });
      
      // Save the filtered notifications back to localStorage
      saveNotificationsToLocalStorage(userId, localNotifications);
      
      console.log(`Falling back to localStorage, found ${localNotifications.length} notifications (after filtering)`);
      return localNotifications;
    }
    
    return [];
  },
  
  // Create a new notification
  createNotification: async (notification) => {
    try {
      const response = await api.post('/notifications', notification);
      return response.data;
    } catch (error) {
      console.error('Error creating notification in API:', error);
      
      // Fallback to localStorage
      const userId = getUserId();
      if (userId) {
        console.log('Saving notification to localStorage instead');
        const newNotification = {
          _id: generateLocalId(),
          ...notification,
          userId: notification.userId || userId,
          timestamp: new Date(),
          read: false
        };
        
        const notifications = getNotificationsFromLocalStorage(userId);
        saveNotificationsToLocalStorage(userId, [newNotification, ...notifications]);
        
        return newNotification;
      }
      throw error;
    }
  },
  
  // Mark a specific notification as read
  markAsRead: async (notificationId) => {
    try {
      const response = await api.put(`/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      console.error('Error marking notification as read in API:', error);
      
      // Fallback to localStorage
      const userId = getUserId();
      if (userId) {
        console.log('Updating notification read status in localStorage');
        const notifications = getNotificationsFromLocalStorage(userId);
        const updatedNotifications = notifications.map(notification => 
          notification._id === notificationId || notification.id === notificationId
            ? { ...notification, read: true } 
            : notification
        );
        
        saveNotificationsToLocalStorage(userId, updatedNotifications);
        return { success: true };
      }
      throw error;
    }
  },
  
  // Mark all notifications as read
  markAllAsRead: async () => {
    try {
      const response = await api.put('/notifications/read');
      return response.data;
    } catch (error) {
      console.error('Error marking all notifications as read in API:', error);
      
      // Fallback to localStorage
      const userId = getUserId();
      if (userId) {
        console.log('Marking all notifications as read in localStorage');
        const notifications = getNotificationsFromLocalStorage(userId);
        const updatedNotifications = notifications.map(notification => ({
          ...notification, 
          read: true
        }));
        
        saveNotificationsToLocalStorage(userId, updatedNotifications);
        return { success: true };
      }
      throw error;
    }
  },
  
  // Delete a specific notification
  deleteNotification: async (notificationId) => {
    try {
      const response = await api.delete(`/notifications/${notificationId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting notification from API:', error);
      
      // Fallback to localStorage
      const userId = getUserId();
      if (userId) {
        console.log('Deleting notification from localStorage');
        const notifications = getNotificationsFromLocalStorage(userId);
        const filteredNotifications = notifications.filter(notification => 
          notification._id !== notificationId && notification.id !== notificationId
        );
        
        saveNotificationsToLocalStorage(userId, filteredNotifications);
        return { success: true };
      }
      throw error;
    }
  },
  
  // Clear all notifications for the current user
  clearAllNotifications: async () => {
    const userId = getUserId();
    let apiSuccess = false;
    
    // Try API first
    try {
      const response = await api.delete('/notifications');
      apiSuccess = true;
      console.log('Successfully cleared notifications from API');
      
      // Also clean up the specific blocked notifications from the API
      const blockedScanIds = ['test430_344473', 'test5_251692', 'test45', 'test', 'oyo_990845', 'Scan_154432', 'housing_819875'];
      for (const scanId of blockedScanIds) {
        try {
          await api.delete(`/notifications/scan/${scanId}`);
          console.log(`Cleaned up API notifications for blocked scan: ${scanId}`);
        } catch (cleanupError) {
          console.log(`Could not clean up API notifications for ${scanId}:`, cleanupError.response?.status === 404 ? 'not found' : cleanupError.message);
        }
      }
    } catch (error) {
      console.error('Error clearing notifications from API:', error);
      isApiAvailable = false; // Mark API as unavailable
    }
    
    // Always clear from localStorage too to ensure consistency
    if (userId) {
      console.log('Clearing all notifications from localStorage');
      saveNotificationsToLocalStorage(userId, []);
      
      // Also clear any other storage keys that might have notifications or processed scans
      try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (
            key.includes('notifications') || 
            key.includes('processedScans') ||
            key.startsWith(`notifications_${userId}`) ||
            key.startsWith(`processedScans_${userId}`)
          )) {
            keysToRemove.push(key);
          }
        }
        
        keysToRemove.forEach(key => {
          console.log(`Clearing localStorage key: ${key}`);
          localStorage.removeItem(key);
        });
        
        // Re-add only the blocked scan IDs to prevent them from coming back
        const blockedScanIds = ['test430_344473', 'test5_251692', 'test45', 'test', 'oyo_990845', 'Scan_154432', 'housing_819875'];
        localStorage.setItem(`processedScans_${userId}`, JSON.stringify(blockedScanIds));
        console.log('Re-added blocked scan IDs to processed scans to prevent reappearance');
      } catch (storageError) {
        console.error('Error clearing localStorage:', storageError);
      }
    }
    
    if (apiSuccess) {
      return { success: true, source: 'api' };
    } else if (userId) {
      return { success: true, source: 'localStorage' };
    } else {
      throw new Error('Unable to clear notifications - no user ID available');
    }
  }
};

export default notificationService; 